import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from 'react';
import { Bug, Image as ImageIcon, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MarkdownEditor } from '../ui/markdown-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { usePermission } from '../rbac/use-permission';
import { useStandort } from '../layouts/use-standort';
import { P } from '../../lib/rbac/permissions.generated';
import {
  kontextZeilen,
  sammleKontext,
  MELDE_EREIGNIS,
  type MeldeAnstoss,
  type MeldeKontext,
} from '../../lib/testing/context';
import { setzeMeldenVerfuegbar } from '../../lib/testing/workflow';
import {
  testingApi,
  SCREENSHOT_MAX_BYTES,
  SCREENSHOT_TYPES,
  TICKET_KIND_LABEL,
  TICKET_KIND_MELDEN,
  TICKET_SEVERITY_LABEL,
  type TicketKind,
  type TicketSeverity,
} from '../../lib/api/testing';

/**
 * Melde-Knopf — von jeder Seite aus erreichbar, auf dem Test- wie dem Livesystem.
 *
 * Der Knopf hängt im Layout, nicht an einer Route: eine Meldung entsteht dort, wo
 * etwas schiefgeht, nicht auf einer eigenen Seite. Mitgeschickt wird der Zustand
 * zum Meldezeitpunkt; was das genau ist, kann jeder vorher aufklappen — gemeldet
 * wird aus echten Kundendaten heraus, da gehört Offenheit dazu.
 */
export default function ReportBugButton() {
  const darfMelden = usePermission(P.testing.tickets.tickets_melden);
  const [freigeschaltet, setFreigeschaltet] = useState<boolean | null>(null);
  const [anstoss, setAnstoss] = useState<MeldeAnstoss | null>(null);

  useEffect(() => {
    if (!darfMelden) return;
    let abgebrochen = false;
    testingApi
      .status()
      .then((daten) => {
        if (abgebrochen) return;
        const an = Boolean(daten?.enabled);
        setFreigeschaltet(an);
        // Assistenten zeigen denselben Knopf in ihrer Kopfzeile; sie sollen die
        // Frage nicht ein zweites Mal stellen.
        setzeMeldenVerfuegbar(an);
      })
      .catch(() => {
        // Ohne Antwort gibt es das Modul hier nicht — dann bleibt der Knopf weg.
        if (abgebrochen) return;
        setFreigeschaltet(false);
        setzeMeldenVerfuegbar(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [darfMelden]);

  // Die ErrorBoundary meldet über ein Ereignis: sie steht oberhalb der Provider
  // und kommt an diesen Zustand sonst nicht heran.
  useEffect(() => {
    if (!darfMelden || freigeschaltet !== true) return;
    const beiMeldung = (ereignis: Event) => {
      const detail = (ereignis as CustomEvent<MeldeAnstoss>).detail ?? {};
      setAnstoss(detail);
    };
    window.addEventListener(MELDE_EREIGNIS, beiMeldung);
    return () => window.removeEventListener(MELDE_EREIGNIS, beiMeldung);
  }, [darfMelden, freigeschaltet]);

  if (!darfMelden || freigeschaltet !== true) return null;

  return (
    <>
      <Button
        type="button"
        onClick={() => setAnstoss({})}
        className="fixed bottom-5 right-5 z-30 gap-2 shadow-surface"
        aria-label="Ticket erstellen"
      >
        <Bug className="h-4 w-4" />
        <span className="hidden sm:inline">Ticket erstellen</span>
      </Button>
      {anstoss !== null && (
        <ReportBugDialog anstoss={anstoss} onClose={() => setAnstoss(null)} />
      )}
    </>
  );
}

function ReportBugDialog({
  anstoss,
  onClose,
}: {
  anstoss: MeldeAnstoss;
  onClose: () => void;
}) {
  const { selectedStandortId, selectedStandort } = useStandort();
  const [titel, setTitel] = useState(anstoss.titel ?? '');
  const [text, setText] = useState('');
  const [art, setArt] = useState<TicketKind>('bug');
  const [schwere, setSchwere] = useState<TicketSeverity>(anstoss.boundary ? 'hoch' : 'mittel');
  const [kontextOffen, setKontextOffen] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [screenshot, setScreenshot] = useState<{ datei: File; vorschau: string } | null>(null);
  const vorschauRef = useRef<string | null>(null);

  // Der Kontext wird beim Öffnen eingefroren: was der Melder gesehen hat, zählt —
  // nicht der Zustand nach dem Tippen.
  const [kontext] = useState<MeldeKontext>(() =>
    sammleKontext({ standortId: selectedStandortId, boundary: anstoss.boundary })
  );

  useEffect(() => {
    // Die Objekt-URL der Vorschau gehört wieder freigegeben, sonst hält der
    // Browser das Bild bis zum Neuladen fest.
    return () => {
      if (vorschauRef.current) URL.revokeObjectURL(vorschauRef.current);
    };
  }, []);

  const uebernehmeBild = useCallback((datei: File) => {
    if (!SCREENSHOT_TYPES.includes(datei.type)) {
      toast.error('Als Screenshot gehen PNG, JPEG und WebP.');
      return;
    }
    if (datei.size > SCREENSHOT_MAX_BYTES) {
      toast.error('Der Screenshot ist zu groß (mehr als 8 MB).');
      return;
    }
    if (vorschauRef.current) URL.revokeObjectURL(vorschauRef.current);
    const vorschau = URL.createObjectURL(datei);
    vorschauRef.current = vorschau;
    setScreenshot({ datei, vorschau });
  }, []);

  // Einfügen aus der Zwischenablage statt Dateiauswahl: Bildschirmfoto machen,
  // Strg+V — das ist der Weg, den ohnehin alle nehmen.
  const beiEinfuegen = (ereignis: ClipboardEvent) => {
    const bild = Array.from(ereignis.clipboardData?.items ?? []).find((eintrag) =>
      eintrag.type.startsWith('image/')
    );
    if (!bild) return;
    const datei = bild.getAsFile();
    if (datei) {
      ereignis.preventDefault();
      uebernehmeBild(datei);
    }
  };

  const entferneBild = () => {
    if (vorschauRef.current) URL.revokeObjectURL(vorschauRef.current);
    vorschauRef.current = null;
    setScreenshot(null);
  };

  const senden = async () => {
    if (!text.trim()) {
      toast.error('Ohne Beschreibung lässt sich nichts nachstellen.');
      return;
    }
    setSendet(true);
    try {
      const ticket = await testingApi.create({
        title: titel.trim() || undefined,
        body: text.trim(),
        kind: art,
        severity: schwere,
        standortId: selectedStandortId,
        context: kontext as unknown as Record<string, unknown>,
      });
      // Das Ticket steht; ein misslungener Screenshot darf es nicht mehr kippen.
      if (screenshot) {
        try {
          await testingApi.uploadScreenshot(ticket.id, screenshot.datei);
        } catch {
          toast.warning(`${ticket.label} ist gespeichert, der Screenshot ging nicht mit.`);
          onClose();
          return;
        }
      }
      toast.success(`Danke — die Meldung liegt als ${ticket.label} im Testing-Bereich.`);
      onClose();
    } catch (fehler) {
      toast.error(
        fehler instanceof Error ? fehler.message : 'Die Meldung konnte nicht gespeichert werden.'
      );
    } finally {
      setSendet(false);
    }
  };

  return (
    <Dialog open onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent className="max-w-lg" onPaste={beiEinfuegen}>
        <DialogHeader>
          <DialogTitle>Ticket erstellen</DialogTitle>
          <DialogDescription>
            Fehler, Idee oder Frage — die Art wählst du unten. Wo du gerade bist, wird
            automatisch mitgeschickt. Screenshot mit Strg+V einfügen.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {anstoss.boundary && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <div className="font-medium">Die Seite ist abgestürzt</div>
              <div className="break-words text-xs text-muted-foreground">
                {anstoss.boundary.message}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="ticket-titel">Titel (optional)</Label>
            <Input
              id="ticket-titel"
              value={titel}
              onChange={(event) => setTitel(event.target.value)}
              placeholder="Kurz in einem Satz"
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ticket-text">Beschreibung</Label>
            <MarkdownEditor
              id="ticket-text"
              value={text}
              onChange={setText}
              rows={6}
              placeholder={'Was hast du getan?\nWas ist passiert?\nWas hättest du erwartet?'}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Art</Label>
              <Select value={art} onValueChange={(wert) => setArt(wert as TicketKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_KIND_MELDEN.map((wert) => (
                    <SelectItem key={wert} value={wert}>
                      {TICKET_KIND_LABEL[wert]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Schweregrad</Label>
              <Select value={schwere} onValueChange={(wert) => setSchwere(wert as TicketSeverity)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TICKET_SEVERITY_LABEL) as TicketSeverity[]).map((wert) => (
                    <SelectItem key={wert} value={wert}>
                      {TICKET_SEVERITY_LABEL[wert]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Screenshot</Label>
            {screenshot ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img
                  src={screenshot.vorschau}
                  alt="Eingefügter Screenshot"
                  className="max-h-48 w-full object-contain"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={entferneBild}
                  className="absolute right-1 top-1 h-7 w-7 p-0"
                  aria-label="Screenshot entfernen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
                Bildschirmfoto machen und hier mit Strg+V einfügen
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-muted/30 p-3">
            <button
              type="button"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setKontextOffen((vorher) => !vorher)}
            >
              {kontextOffen ? '▾' : '▸'} Das wird mitgeschickt
              {kontext.requests.length > 0 || kontext.console.length > 0
                ? ` (${kontext.requests.length} Aufrufe, ${kontext.console.length} Konsolenzeilen)`
                : ''}
            </button>
            {kontextOffen && (
              <dl className="mt-3 space-y-1 text-xs">
                {kontextZeilen(kontext, selectedStandort?.name).map(([begriff, wert], index) => (
                  <div key={`${begriff}-${index}`} className="flex gap-2">
                    <dt className="w-24 shrink-0 text-muted-foreground">{begriff}</dt>
                    <dd className="break-all">{wert}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={sendet}>
            Abbrechen
          </Button>
          <Button type="button" onClick={senden} disabled={sendet} className="gap-2">
            {sendet ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Melden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
